import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { getTheme } from '../theme'

export type ColorSchemePreference = 'light' | 'dark' | 'system'
export type ResolvedColorScheme = 'light' | 'dark'

export const COLOR_SCHEME_STORAGE_KEY = 'veilstream-color-scheme'

interface ColorSchemeContextType {
  preference: ColorSchemePreference
  resolved: ResolvedColorScheme
  setPreference: (preference: ColorSchemePreference) => void
}

const ColorSchemeContext = createContext<ColorSchemeContextType | undefined>(undefined)

export const useColorScheme = () => {
  const context = useContext(ColorSchemeContext)
  if (context === undefined) {
    throw new Error('useColorScheme must be used within a ThemeProvider')
  }
  return context
}

/** @deprecated Use useColorScheme. Kept so older imports do not crash. */
export const useTheme = useColorScheme

function readPreference(): ColorSchemePreference {
  try {
    const stored = localStorage.getItem(COLOR_SCHEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    /* private mode */
  }
  return 'system'
}

function prefersLight(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: light)').matches
}

function resolvePreference(preference: ColorSchemePreference): ResolvedColorScheme {
  if (preference === 'light' || preference === 'dark') return preference
  return prefersLight() ? 'light' : 'dark'
}

function applyResolvedScheme(resolved: ResolvedColorScheme) {
  const root = document.documentElement
  root.setAttribute('data-theme', resolved)
  root.style.colorScheme = resolved
}

interface ThemeProviderProps {
  children: ReactNode
}

export const CustomThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ColorSchemePreference>(readPreference)
  const [resolved, setResolved] = useState<ResolvedColorScheme>(() => resolvePreference(readPreference()))

  const setPreference = useCallback((next: ColorSchemePreference) => {
    setPreferenceState(next)
    try {
      localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
    const nextResolved = resolvePreference(next)
    setResolved(nextResolved)
    applyResolvedScheme(nextResolved)
  }, [])

  useEffect(() => {
    const next = resolvePreference(preference)
    setResolved(next)
    applyResolvedScheme(next)
  }, [preference])

  useEffect(() => {
    if (preference !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = () => {
      const next = resolvePreference('system')
      setResolved(next)
      applyResolvedScheme(next)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [preference])

  const muiTheme = useMemo(() => getTheme('Evergreen Dark', resolved), [resolved])

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  )

  return (
    <ColorSchemeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorSchemeContext.Provider>
  )
}
